import type {
  CrmProfileSection,
  CrmProfileSectionItem,
  CrmProfileSectionTone,
} from "@ease/crm/profile-sections";
import {
  getCrmActionHrefKind,
  normalizeCrmActionHref,
  shouldOpenCrmActionHrefInNewTab,
} from "@ease/crm/safe-action-hrefs";
import { ExternalLink } from "lucide-react";
import { cn } from "@/lib/utils";

export type { CrmProfileSection, CrmProfileSectionItem };

interface CrmProfileSectionsCardProps {
  sections: CrmProfileSection[];
  title: string;
  description: string;
  className?: string;
  titleClassName?: string;
  descriptionClassName?: string;
  itemClassName?: string;
  actionClassName?: string;
}

interface CrmAdditionalProfileFieldsCardProps {
  fields: CrmProfileSectionItem[];
  title?: string;
  description?: string;
  className?: string;
  itemClassName?: string;
  actionClassName?: string;
}

export function CrmProfileSectionsCard({
  sections,
  title,
  description,
  className,
  titleClassName,
  descriptionClassName,
  itemClassName,
  actionClassName,
}: CrmProfileSectionsCardProps) {
  if (sections.length === 0) return null;

  return (
    <section className={cn("rounded-xl border border-[var(--color-border)] bg-[var(--color-card)] p-4", className)}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className={cn("text-xs font-medium uppercase tracking-wider text-[var(--color-muted-foreground)]", titleClassName)}>
            {title}
          </h3>
          <p className={cn("mt-1 text-sm text-[var(--color-muted-foreground)]", descriptionClassName)}>
            {description}
          </p>
        </div>
        <span className="shrink-0 rounded-full border border-[var(--color-border)] px-2.5 py-1 text-xs font-medium text-[var(--color-muted-foreground)]">
          {sections.length}
        </span>
      </div>

      <div className="mt-4 space-y-4">
        {sections.map((section) => (
          <section key={section.key} className="border-t border-[var(--color-border)] pt-4 first:border-t-0 first:pt-0">
            <div>
              <h4 className="text-sm font-semibold text-[var(--color-foreground)]">{section.label}</h4>
              {section.description ? (
                <p className="mt-1 text-xs text-[var(--color-muted-foreground)]">{section.description}</p>
              ) : null}
            </div>
            <div className="mt-3 grid gap-2 md:grid-cols-2">
              {section.items.map((item) => (
                <CrmProfileSectionItemCell
                  key={`${section.key}:${item.key}`}
                  item={item}
                  className={itemClassName}
                  actionClassName={actionClassName}
                />
              ))}
            </div>
          </section>
        ))}
      </div>
    </section>
  );
}

export function CrmAdditionalProfileFieldsCard({
  fields,
  title = "Additional profile fields",
  description,
  className,
  itemClassName,
  actionClassName,
}: CrmAdditionalProfileFieldsCardProps) {
  if (fields.length === 0) return null;

  return (
    <section className={cn("rounded-xl border border-[var(--color-border)] bg-[var(--color-card)] p-4", className)}>
      <h3 className="text-xs font-medium uppercase text-[var(--color-muted-foreground)]">{title}</h3>
      {description ? (
        <p className="mt-1 text-sm text-[var(--color-muted-foreground)]">{description}</p>
      ) : null}
      <div className="mt-3 grid gap-2 md:grid-cols-2">
        {fields.map((field) => (
          <CrmProfileSectionItemCell
            key={field.key}
            item={field}
            className={itemClassName}
            actionClassName={actionClassName}
          />
        ))}
      </div>
    </section>
  );
}

export function CrmProfileSectionItemCell({
  item,
  className,
  actionClassName,
}: {
  item: CrmProfileSectionItem;
  className?: string;
  actionClassName?: string;
}) {
  return (
    <div className={cn("rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2", className)}>
      <div className="flex items-start justify-between gap-2">
        <p className="text-[11px] font-semibold uppercase text-[var(--color-muted-foreground)]">{item.label}</p>
        {item.tone !== "neutral" ? (
          <span className={cn("shrink-0 rounded-full border px-2 py-0.5 text-[10px] font-medium capitalize", getCrmProfileToneClasses(item.tone))}>
            {item.tone}
          </span>
        ) : null}
      </div>
      <p className="mt-1 break-words text-sm text-[var(--color-foreground)]">{item.value}</p>
      {item.description ? (
        <p className="mt-1 text-xs text-[var(--color-muted-foreground)]">{item.description}</p>
      ) : null}
      {item.href ? <CrmProfileSectionItemActionLink item={item} className={actionClassName} /> : null}
    </div>
  );
}

function CrmProfileSectionItemActionLink({
  item,
  className,
}: {
  item: CrmProfileSectionItem;
  className?: string;
}) {
  if (!item.href) return null;
  const href = normalizeCrmActionHref(item.href);
  if (!href) return null;

  const hrefKind = getCrmActionHrefKind(href);
  if (!hrefKind) return null;

  const label = item.actionLabel ?? "Open";
  const openInNewTab = shouldOpenCrmActionHrefInNewTab(href);

  return (
    <a
      href={href}
      target={openInNewTab ? "_blank" : undefined}
      rel={openInNewTab ? "noreferrer" : undefined}
      className={cn(
        "mt-2 inline-flex items-center gap-1 rounded-md border border-[var(--color-border)] px-2.5 py-1 text-xs font-medium text-[var(--color-foreground)] transition-colors hover:bg-[var(--color-muted)]",
        className,
      )}
    >
      {label}
      <ExternalLink className="h-3 w-3" />
    </a>
  );
}

export function getCrmProfileToneClasses(tone: CrmProfileSectionTone): string {
  if (tone === "success") {
    return "border-[var(--color-success)]/30 bg-[var(--color-success)]/10 text-[var(--color-success)]";
  }
  if (tone === "warning") {
    return "border-[var(--color-warning)]/30 bg-[var(--color-warning)]/10 text-[var(--color-warning)]";
  }
  if (tone === "danger") {
    return "border-[var(--color-destructive)]/30 bg-[var(--color-destructive)]/10 text-[var(--color-destructive)]";
  }
  if (tone === "info") {
    return "border-[var(--color-primary)]/20 bg-[var(--color-primary)]/5 text-[var(--color-primary)]";
  }
  return "border-[var(--color-border)] bg-[var(--color-card)] text-[var(--color-muted-foreground)]";
}
